/**
 * Skill Validation Hook
 *
 * Provides real-time validation of skill configurations with debouncing.
 * Calls the backend validation API and returns errors/warnings.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { skillsApi, type ValidationError, type SkillValidationResponse } from '@/lib/api/skills';
import type { SkillConfig } from '@/types';

interface UseSkillValidationOptions {
  /** Debounce delay in milliseconds (default: 500ms) */
  debounceMs?: number;
  /** Whether to validate automatically on change (default: true) */
  autoValidate?: boolean;
}

interface UseSkillValidationResult {
  /** Whether there are validation errors (not including warnings) */
  isValid: boolean;
  /** List of validation errors */
  errors: ValidationError[];
  /** List of validation warnings */
  warnings: ValidationError[];
  /** Whether validation is in progress */
  isValidating: boolean;
  /** Manually trigger validation */
  validate: () => Promise<SkillValidationResponse | null>;
  /** Get errors for a specific skill */
  getSkillErrors: (skillId: string) => ValidationError[];
  /** Get warnings for a specific skill */
  getSkillWarnings: (skillId: string) => ValidationError[];
  /** Clear all validation results */
  clear: () => void;
}

/**
 * Hook for validating skill configurations
 *
 * @example
 * ```tsx
 * const { isValid, errors, warnings, validate } = useSkillValidation(skills);
 *
 * // Show validation status
 * {!isValid && <Alert variant="error">Fix errors before saving</Alert>}
 *
 * // Show skill-specific errors
 * {getSkillErrors(skill.skill_id).map(err => (
 *   <div key={err.field}>{err.message}</div>
 * ))}
 * ```
 */
export function useSkillValidation(
  skills: SkillConfig[],
  options: UseSkillValidationOptions = {}
): UseSkillValidationResult {
  const { debounceMs = 500, autoValidate = true } = options;

  const [isValid, setIsValid] = useState(true);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [warnings, setWarnings] = useState<ValidationError[]>([]);
  const [isValidating, setIsValidating] = useState(false);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Validate skills
  const validate = useCallback(async (): Promise<SkillValidationResponse | null> => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Skip validation if no skills
    if (!skills || skills.length === 0) {
      setIsValid(true);
      setErrors([]);
      setWarnings([]);
      return { valid: true, errors: [], warnings: [] };
    }

    setIsValidating(true);
    abortControllerRef.current = new AbortController();

    try {
      const result = await skillsApi.validate(skills);
      setIsValid(result.valid);
      setErrors(result.errors);
      setWarnings(result.warnings);
      return result;
    } catch (error) {
      // Ignore abort errors
      if (error instanceof Error && error.name === 'AbortError') {
        return null;
      }
      console.error('Skill validation failed:', error);
      // On error, assume valid to not block the user
      setIsValid(true);
      setErrors([]);
      setWarnings([]);
      return null;
    } finally {
      setIsValidating(false);
    }
  }, [skills]);

  // Auto-validate with debounce
  useEffect(() => {
    if (!autoValidate) return;

    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      validate();
    }, debounceMs);

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [skills, debounceMs, autoValidate, validate]);

  // Get errors for a specific skill
  const getSkillErrors = useCallback(
    (skillId: string): ValidationError[] => {
      return errors.filter((e) => e.skill_id === skillId);
    },
    [errors]
  );

  // Get warnings for a specific skill
  const getSkillWarnings = useCallback(
    (skillId: string): ValidationError[] => {
      return warnings.filter((w) => w.skill_id === skillId);
    },
    [warnings]
  );

  // Clear all validation results
  const clear = useCallback(() => {
    setIsValid(true);
    setErrors([]);
    setWarnings([]);
  }, []);

  return {
    isValid,
    errors,
    warnings,
    isValidating,
    validate,
    getSkillErrors,
    getSkillWarnings,
    clear,
  };
}

export default useSkillValidation;
